/**
 * LogOutButton - Global logout button component
 * Provides consistent logout functionality across the application
 */
import { useState } from 'react'
import { useAuth } from '@/global/auth/AuthContext'
import { Button } from '@/global/components/ui/button'
import { LogOut } from 'lucide-react'

interface LogOutButtonProps {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  children?: React.ReactNode
  className?: string
  showIcon?: boolean
}

export function LogOutButton({ 
  variant = 'outline',
  size = 'default',
  children,
  className,
  showIcon = true
}: LogOutButtonProps) {
  const { logout } = useAuth()
  const [isLoading, setIsLoading] = useState(false)

  const handleLogout = async () => {
    setIsLoading(true)
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
      // Continue with logout even if there's an error
    } finally {
      // Force a page refresh and redirect to login
      // This ensures clean state and immediate redirect
      window.location.href = '/login'
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleLogout}
      disabled={isLoading}
      className={className}
    >
      {showIcon && <LogOut className="h-4 w-4 mr-2" />}
      {children || (isLoading ? 'Signing out...' : 'Sign Out')}
    </Button>
  )
}

export default LogOutButton
